import React, { useContext, useRef } from "react"
import PropTypes from "prop-types"
import { mediaQuery } from "@zendeskgarden/react-theming"
import { Modal, Close as CloseModalButton } from "@zendeskgarden/react-modals"
import styled, { ThemeContext } from "styled-components"
import { Field, Label, MediaInput } from "@zendeskgarden/react-forms"
import SearchIcon from "@zendeskgarden/svg-icons/src/16/search-stroke.svg"
import SearchModalContext from "./searchModalContext"
import useSearchCallback from "../../lib/hooks/useSearchCallback"
import useSearchModalKeyboardNav from "../../lib/hooks/useSearchModalKeyboardNav"
import SearchModalBody from "./searchModalBody"
import { lightTheme } from "../../styles/themes"
import { collections } from "../../lib/prop-types"

const StyledModal = styled(Modal)`
  top: ${props => props.theme.space.lg};
  padding: ${props => props.theme.space.lg};
  background-color: ${props => props.theme.colors.search.backgroundColor};
  border: ${props =>
    `${props.theme.borders.sm} ${props.theme.colors.document.horizontalRule}`};

  div[data-garden-id="forms.faux_input"] {
    margin-top: 0;
    margin-right: ${props => props.theme.space.sm};
    font-weight: initial;
    border-color: ${props => props.theme.colors.search.inputBorder};
  }

  ${props => mediaQuery("down", "sm", props.theme)} {
    top: 0;
    right: 0;
    left: 0;
    width: 100%;
    min-height: 100vh;
    padding-right: ${props => props.theme.space.horz.md};
    padding-left: ${props => props.theme.space.horz.md};
    border-radius: 0;
  }
`

const StyledCloseModalButton = styled(CloseModalButton)`
  position: initial;
  display: inline-block;
  flex-shrink: 0;
  color: ${props => props.theme.colors.search.closeButtonColor};

  &:hover {
    color: ${props => props.theme.colors.search.closeButtonColor};
    background-color: ${props => props.theme.colors.search.closeButtonHover};
  }
`

const StyledField = styled(Field)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.space.lg};
`

const StyledSearchIcon = styled(SearchIcon)`
  color: ${props => props.theme.colors.search.searchBarIcon};
`

const SearchModal = ({ closeModal, collection }) => {
  const { client, hits, setHits, searchTerm, setSearchTerm } =
    useContext(SearchModalContext)

  const handleSearchInput = useSearchCallback(client, setHits, 200)
  const inputRef = useRef(null)
  useSearchModalKeyboardNav(inputRef)

  const darkThemeBackdrop = { style: { backgroundColor: "rgba(1,24,27,0.75)" } }

  const backdropProps =
    useContext(ThemeContext) === lightTheme ? {} : darkThemeBackdrop

  return (
    // focusOnMount and restoreFocus need to be explicitly set to false below to
    // allow the search input field to capture keyboard focus on component mount
    <StyledModal
      onClose={closeModal}
      backdropProps={backdropProps}
      focusOnMount={false}
      restoreFocus={false}
    >
      <StyledField>
        <Label hidden>Search</Label>
        <MediaInput
          value={searchTerm}
          ref={inputRef}
          autoFocus
          onChange={e => {
            setHits({
              ...hits,
              documentationLoading: true,
              apiReferenceLoading: true
            })
            setSearchTerm(e.target.value)
            handleSearchInput(e.target.value)
          }}
          start={<StyledSearchIcon />}
        />
        <StyledCloseModalButton aria-label="Close modal" />
      </StyledField>
      <SearchModalBody
        documentationResults={hits.documentation}
        documentationLoading={hits.documentationLoading}
        apiReferenceResults={hits.apiReference}
        apiReferenceLoading={hits.apiReferenceLoading}
        searchTerm={searchTerm}
        collection={collection}
      />
    </StyledModal>
  )
}

SearchModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  collection: collections
}

SearchModal.defaultProps = {
  collection: "documentation"
}

export default SearchModal
